import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getMonitoringSettings, updateDomMode } from '@/services/api'
import type { DomMode } from '@/types'

const MODES: { value: DomMode; label: string; title: string }[] = [
  {
    value: 'both',
    label: 'Оба',
    title: 'Показывать и точные, и агрегированные лимитки',
  },
  {
    value: 'precise',
    label: 'Точные',
    title: 'Только точные: каждый уровень стакана отдельно',
  },
  {
    value: 'aggregated',
    label: 'Агрегированные',
    title: 'Только агрегированные: заявки на близких ценах суммируются по целой цене',
  },
]

/**
 * Selector for the DOM display mode ("precise" / "aggregated" / "both").
 * The bot always detects and stores both types and sends both to Telegram;
 * this only filters what the dashboard shows. The value is shared across
 * browsers and can also be changed from the Telegram bot.
 */
export function DomModeSelector() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringSettings'],
    queryFn: getMonitoringSettings,
    staleTime: 10000,
    // Reflect changes made elsewhere (e.g. the Telegram bot) without a reload.
    refetchInterval: 15000,
  })

  const mutation = useMutation({
    mutationFn: updateDomMode,
    onSuccess: (settings) => {
      queryClient.setQueryData(['monitoringSettings'], settings)
    },
  })

  const current = data?.dom_mode

  return (
    <div className="flex items-center gap-1" title="Режим отслеживания лимиток">
      <span className="text-xs text-text-secondary hidden sm:inline">Лимитки:</span>
      <div className="flex rounded border border-chart-grid overflow-hidden">
        {MODES.map((mode) => {
          const isActive = current === mode.value
          return (
            <button
              key={mode.value}
              onClick={() => !isActive && mutation.mutate(mode.value)}
              disabled={isLoading || mutation.isPending}
              title={mode.title}
              className={`px-2.5 py-1 text-xs transition-colors disabled:opacity-50
                ${isActive
                  ? 'bg-bid/20 text-text-primary font-medium'
                  : 'bg-chart-bg text-text-secondary hover:text-text-primary'}`}
            >
              {mode.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default DomModeSelector
